/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Divider, HelpTip, Text, Stack } from 'sarsaparilla';
import { gnoment } from 'shared-ui/gnoment';
import cx from 'classnames';
import { QUEUING_LOTTERY_STATUS } from 'ui-queuing-lottery';

export default function QueuingLotteryWidget({
    permit,
    lotteries,
    onEnterLottery,
    futureLotteryTitle,
    isFutureLottery,
    isLoadingButton = false,
}) {
    const QUEUING_LOTTERY_RANGE_FORMAT = 'ddd, MMM D, YYYY';
    function formatDate(stringDate) {
        return gnoment
            .tz(stringDate, permit?.time_zone)
            .format(QUEUING_LOTTERY_RANGE_FORMAT);
    }

    function formatDateTime(stringDate) {
        return gnoment
            .tz(stringDate, permit?.time_zone)
            .format(`${QUEUING_LOTTERY_RANGE_FORMAT} | h:mm A z`);
    }
    const lotteriesToRender = lotteries.map((lottery, index) => {
        const isOpen = lottery.status === QUEUING_LOTTERY_STATUS.Open;
        return (
            <Box key={lottery.lottery_id}>
                {index > 0 && (
                    <Box
                        className={cx('mb-2', {
                            'rec-aside-subheader': isOpen,
                            'rec-aside-subheader-gray': !isOpen,
                        })}
                    >
                        <div>{isOpen ? 'Early Access Lottery' : 'Future Lottery'}</div>
                    </Box>
                )}

                {isOpen ? (
                    <>
                        <Text fontWeight="semibold">Application Deadline</Text>
                        <Text>{formatDateTime(lottery.close_time)}</Text>
                    </>
                ) : (
                    <>
                        <Text fontWeight="semibold">Application Opens</Text>
                        <Text>{formatDateTime(lottery.open_time)}</Text>
                    </>
                )}

                <Divider />
                <Text fontWeight="semibold">Lottery Results Available</Text>
                <Text>{formatDate(lottery.announce_time)} (Estimated)</Text>

                <Divider />
                <Text fontWeight="semibold">
                    Early Access Period
                    <HelpTip
                        tipForDescriptor="Help Tip for Early Access Period"
                        maxWidth={260}
                        content="Successful lottery applicants may view real-time inventory availability as soon as the Early Access Period opens. Successful applicants may attempt to make a reservation beginning on their access window date and time."
                    />
                </Text>
                <Text>
                    {formatDate(lottery.eap_start)} - {formatDate(lottery.eap_end)}
                </Text>

                <Divider />
                <Text>
                    This lottery provides an opportunity to access permit inventory early
                    and book a reservation for the following dates:
                </Text>
                <Text fontWeight="semibold">
                    {formatDate(lottery.trip_range_start)} -{' '}
                    {formatDate(lottery.trip_range_end)}
                </Text>

                {isOpen && (
                    <Button
                        className="mt-2"
                        shouldFitContainer
                        isLoading={isLoadingButton}
                        onClick={
                            onEnterLottery ? () => onEnterLottery(lottery) : undefined
                        }
                    >
                        Register for Open Lottery
                    </Button>
                )}
            </Box>
        );
    });

    return (
        <Stack className="right-rail-card">
            {futureLotteryTitle && isFutureLottery && (
                <div className="rec-aside-subheader-gray">
                    <div>{futureLotteryTitle}</div>
                </div>
            )}
            {lotteriesToRender}
        </Stack>
    );
}

QueuingLotteryWidget.propTypes = {
    lotteries: PropTypes.array,
    onEnterLottery: PropTypes.func,
    isLoadingButton: PropTypes.bool,
    permit: PropTypes.object,
    futureLotteryTitle: PropTypes.string,
    isFutureLottery: PropTypes.bool,
};
